import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { useForgotPassword } from '@/api/auth/use-auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const ForgotPasswordPage: React.FC = () => {
    const { t } = useTranslation('auth');
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const { mutate: forgotPassword, isPending } = useForgotPassword();

    const [email, setEmail] = useState('');
    const [errors, setErrors] = useState<Record<string, string[]>>({});
    const [isSubmitted, setIsSubmitted] = useState(false);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        forgotPassword(
            { email },
            {
                onSuccess: () => {
                    setIsSubmitted(true);
                    enqueueSnackbar(t('resetInstructionsSent'), { variant: 'success' });
                },
                onError: (error: any) => {
                    if (error.response?.data?.errors) {
                        setErrors(error.response.data.errors);
                    } else {
                        enqueueSnackbar(t('failedToSendReset'), { variant: 'error' });
                    }
                },
            }
        );
    };

    if (isSubmitted) {
        return (
            <>
                <div className="bg-green-100 text-green-800 px-4 py-3 rounded mb-4 text-center">
                    {t('resetInstructionsSent')}
                </div>
                <p className="text-center text-muted-foreground mb-6">
                    {t('resetInstructionsSentDetail')}
                </p>
                <Button className="w-full" onClick={() => navigate('/auth/login')}>
                    {t('returnToLogin')}
                </Button>
            </>
        );
    }

    return (
        <>
            <h1 className="mb-2 text-center text-[length:var(--auth-title-size)] font-bold leading-tight tracking-tight">
                {t('forgotPasswordTitle')}
            </h1>
            <p className="text-muted-foreground text-center mb-8">{t('enterEmailForReset')}</p>

            <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                    <Label htmlFor="email">{t('emailAddress')}</Label>
                    <Input
                        id="email"
                        type="email"
                        value={email}
                        onChange={e => {
                            setEmail(e.target.value);
                            if (errors.email) {
                                setErrors(prev => ({ ...prev, email: [] }));
                            }
                        }}
                        required
                        className={errors.email ? 'border-destructive' : ''}
                    />
                    {errors.email && <p className="text-sm text-destructive">{errors.email[0]}</p>}
                </div>

                <Button type="submit" className="w-full" disabled={isPending}>
                    {isPending ? t('sending') : t('sendResetInstructions')}
                </Button>

                <div className="text-center">
                    <p className="text-sm text-muted-foreground">
                        {t('rememberPassword')}{' '}
                        <Link to="/auth/login" className="text-primary hover:underline">
                            {t('signIn')}
                        </Link>
                    </p>
                </div>
            </form>
        </>
    );
};

export default ForgotPasswordPage;
